// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';
import { useState, useEffect, memo, useCallback, useRef } from 'react';
import { 
  Plus, Pencil, Trash2, X, Search, Building2, ArrowUpDown, 
  Filter, Download, Check, RefreshCw, User, Zap, CreditCard, Tag
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { usePayeesStore, clearPayees } from '@flows.cash/core';
import { useAuthStore } from '@/store/authStore';
import { useCurrenciesStore } from '@/store/currenciesStore';
// Migrated: Using native Date instead of Firebase Timestamp
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from '@/components/ui/badge';
import { formatDateOnly } from '@/lib/dateUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { motion, AnimatePresence } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useHotkeys } from 'react-hotkeys-hook';
import { toast as notify } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const tableAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.2 }
};

const rowAnimation = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 },
  transition: { duration: 0.15 }
};

// Define a more specific interface for the Payee that includes icon and color
interface PayeeWithUI {
  id: string;
  user_id: string;
  name: string;
  type: string;
  icon?: string;
  color?: string;
  website?: string;
  notes?: string;
  total_transactions: number;
  created_at: string;
  updated_at: string;
  isActive?: boolean;
}

export default function PayeesSettings() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { payees, isLoading, fetchPayees, addPayee, updatePayee, deletePayee } = usePayeesStore();
  const { currencies } = useCurrenciesStore();
  const defaultCurrency = currencies.find(c => c.isDefault);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPayee, setSelectedPayee] = useState<PayeeWithUI | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'general',
    icon: '',
    color: '',
    website: '',
    notes: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{ key: keyof PayeeWithUI; direction: 'asc' | 'desc' } | null>(null);

  // Keyboard shortcuts
  useHotkeys('ctrl+f', (e: KeyboardEvent) => {
    e.preventDefault()
    document.getElementById('search-payees')?.focus()
  });
  useHotkeys('ctrl+n', () => setIsAddDialogOpen(true));
  useHotkeys('esc', () => {
    setSearchQuery('');
    setCategoryFilter('');
  });

  const uniqueCategories = Array.from(new Set(payees.map(p => p.type).filter(Boolean)));

  const filteredPayees = payees
    .filter(payee => 
      payee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (payee.type && payee.type.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .filter(payee => !categoryFilter || payee.type === categoryFilter)
    .sort((a, b) => {
      if (!sortConfig) return 0;
      
      const key = sortConfig.key;
      // Safely access properties on the objects using their known keys
      const aValue = key === 'icon' || key === 'color' || key === 'isActive' 
        ? (a as PayeeWithUI)[key] 
        : a[key as keyof typeof a];
        
      const bValue = key === 'icon' || key === 'color' || key === 'isActive'
        ? (b as PayeeWithUI)[key]
        : b[key as keyof typeof b];
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      // Compare values
      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

  const handleSort = useCallback((key: keyof PayeeWithUI) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const handleExport = () => {
    const data = filteredPayees.map(({ name, type, total_transactions, updated_at }) => ({
      name,
      type,
      transactions: total_transactions || 0,
      lastTransaction: updated_at ? formatDateOnly(updated_at) : '',
    }));
    
    const csv = [
      ['Name', 'Type', 'Transaction Count', 'Last Transaction'],
      ...data.map(row => Object.values(row)),
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payees-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Force refresh payees from database
  const handleRefreshPayees = async () => {
    if (!user?.id) return;
    notify.info(t('settings.payeesSettings.messages.refreshing'));
    try {
      // Clear the cached payees
      clearPayees();
      // Fetch fresh data from database
      await fetchPayees(user.id);
      notify.success(t('settings.payeesSettings.messages.refreshSuccess'));
    } catch (error) {
      console.error('Error refreshing payees:', error);
      notify.error(t('settings.payeesSettings.messages.error'));
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchPayees(user.id);
    }
  }, [user?.id, fetchPayees]);

  useEffect(() => {
    if (selectedPayee && isEditDialogOpen) {
      setFormData({
        name: selectedPayee.name,
        type: selectedPayee.type || 'general',
        icon: selectedPayee.icon || '',
        color: selectedPayee.color || '',
        website: selectedPayee.website || '',
        notes: selectedPayee.notes || '',
      });
    } else if (!isEditDialogOpen && !isAddDialogOpen) {
      resetForm();
    }
  }, [selectedPayee, isEditDialogOpen, isAddDialogOpen]);

  const handleAddPayee = async (data: typeof formData) => {
    if (!user?.id || !data.name.trim()) return;

    try {
      await addPayee({
        name: data.name.trim(),
        type: data.type,
        icon: data.icon.trim(),
        color: data.color.trim(),
        website: data.website.trim(),
        notes: data.notes.trim(),
        user_id: user.id,
      });
      setIsAddDialogOpen(false);
      resetForm();
      notify.success(t('settings.payeesSettings.messages.addSuccess'));
    } catch (error) {
      console.error('Error adding payee:', error);
      notify.error(t('settings.payeesSettings.messages.error'));
    }
  };

  const handleEditPayee = async (data: typeof formData) => {
    if (!selectedPayee?.id || !data.name.trim()) return;

    try {
      await updatePayee(selectedPayee.id, {
        name: data.name.trim(),
        type: data.type,
        icon: data.icon.trim(),
        color: data.color.trim(),
        website: data.website.trim(),
        notes: data.notes.trim(),
      });
      setIsEditDialogOpen(false);
      setSelectedPayee(null);
      notify.success(t('settings.payeesSettings.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating payee:', error);
      notify.error(t('settings.payeesSettings.messages.error'));
    }
  };

  const payeeIdForDeletion = useRef<string | null>(null);
  const [payeeNameForDeletion, setPayeeNameForDeletion] = useState<string | null>(null);

  const handleDeletePayee = async () => {
    if (!user?.id || !payeeIdForDeletion.current) {
      console.error('Delete payee failed: Missing user ID or payee ID', { 
        userId: user?.id, 
        payeeId: payeeIdForDeletion.current,
      });
      notify.error(t('settings.payeesSettings.messages.error'));
      return;
    }

    console.log('Attempting to delete payee:', { 
      userId: user.id, 
      payeeId: payeeIdForDeletion.current,
      payeeName: payeeNameForDeletion
    });

    try {
      console.log('Calling deletePayee function with:', user.id, payeeIdForDeletion.current);
      await deletePayee(user.id, payeeIdForDeletion.current);
      console.log('Delete payee operation completed successfully');
      
      // Close dialog and clear selection after successful deletion
      setIsDeleteDialogOpen(false);
      setSelectedPayee(null);
      
      // Show success notification
      notify.success(t('settings.payeesSettings.messages.deleteSuccess', { name: payeeNameForDeletion }));
    } catch (error) {
      console.error('Error deleting payee:', error);
      notify.error(t('settings.payeesSettings.messages.error'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'general',
      icon: '',
      color: '',
      website: '',
      notes: '',
    });
    setSelectedPayee(null);
  };

  const onClose = () => {
    resetForm();
    setIsAddDialogOpen(false);
    setIsEditDialogOpen(false);
    setSelectedPayee(null);
  };

  const PayeeDialog = memo(({ isOpen, mode }: { isOpen: boolean; mode: 'add' | 'edit' }) => {
    const { t } = useTranslation();
    const [localFormData, setLocalFormData] = useState({
      name: '',
      type: 'general',
      icon: '',
      color: '',
      website: '',
      notes: '',
    });

    useEffect(() => {
      if (mode === 'edit' && selectedPayee) {
        setLocalFormData({
          name: selectedPayee.name,
          type: selectedPayee.type || 'general',
          icon: selectedPayee.icon || '',
          color: selectedPayee.color || '',
          website: selectedPayee.website || '',
          notes: selectedPayee.notes || '',
        });
      } else {
        setLocalFormData({
          name: '',
          type: 'general',
          icon: '',
          color: '',
          website: '',
          notes: '',
        });
      }
    }, [selectedPayee, mode]);

    const handleLocalInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setLocalFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name: string, value: string) => {
      setLocalFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (mode === 'add') {
        handleAddPayee(localFormData);
      } else {
        handleEditPayee(localFormData);
      }
    };

    const payeeTypes = [
      { value: 'general', label: t('settings.payeesSettings.type.general') },
      { value: 'business', label: t('settings.payeesSettings.type.business') },
      { value: 'person', label: t('settings.payeesSettings.type.person') },
      { value: 'utility', label: t('settings.payeesSettings.type.utility') },
      { value: 'subscription', label: t('settings.payeesSettings.type.subscription') },
    ];

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
              {t(`settings.payeesSettings.${mode}Title`)}
            </DialogTitle>
            <DialogDescription className="text-text-secondary dark:text-text-dark-secondary">
              {t('settings.payeesSettings.formDescription')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="payee-name" className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                  {t('common.name')}
                  <span className="text-error">*</span>
                </Label>
                <div className="relative group">
                  <Input
                    type="text"
                    id="payee-name"
                    name="name"
                    value={localFormData.name}
                    onChange={handleLocalInputChange}
                    placeholder={t('settings.payeesSettings.form.namePlaceholder')}
                    maxLength={50}
                    required
                    className="h-11 rounded-xl border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-text-primary dark:text-text-dark-primary shadow-sm focus:ring-2 focus:ring-primary transition-all duration-200 pr-12"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-secondary">
                    {localFormData.name.length}/50
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="payee-type" className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                  {t('common.type')}
                  <span className="text-error">*</span>
                </Label>
                <Select
                  value={localFormData.type}
                  onValueChange={(value) => handleSelectChange('type', value)}
                >
                  <SelectTrigger className="h-11 rounded-xl border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-text-primary dark:text-text-dark-primary shadow-sm focus:ring-2 focus:ring-primary">
                    <SelectValue placeholder={t('settings.payeesSettings.form.typePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {payeeTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {t(`settings.payeesSettings.type.${type.value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="payee-icon" className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                  {t('common.icon')}
                  <Badge variant="secondary" className="bg-primary/10 text-primary dark:bg-primary/20">{t('common.optional')}</Badge>
                </Label>
                <div className="relative group">
                  <Input
                    type="text"
                    id="payee-icon"
                    name="icon"
                    value={localFormData.icon}
                    onChange={handleLocalInputChange}
                    placeholder={t('settings.payeesSettings.form.iconPlaceholder')}
                    maxLength={30}
                    className="h-11 rounded-xl border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-text-primary dark:text-text-dark-primary shadow-sm focus:ring-2 focus:ring-primary transition-all duration-200 pr-12"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-secondary">
                    {localFormData.icon.length}/30
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="payee-color" className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                  {t('common.color')}
                  <Badge variant="secondary" className="bg-primary/10 text-primary dark:bg-primary/20">{t('common.optional')}</Badge>
                </Label>
                <div className="relative group">
                  <Input
                    type="text"
                    id="payee-color"
                    name="color"
                    value={localFormData.color}
                    onChange={handleLocalInputChange}
                    placeholder={t('settings.payeesSettings.form.colorPlaceholder')}
                    maxLength={30}
                    className="h-11 rounded-xl border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-text-primary dark:text-text-dark-primary shadow-sm focus:ring-2 focus:ring-primary transition-all duration-200 pr-12"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-secondary">
                    {localFormData.color.length}/30
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="payee-website" className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                  {t('common.website')}
                  <Badge variant="secondary" className="bg-primary/10 text-primary dark:bg-primary/20">{t('common.optional')}</Badge>
                </Label>
                <div className="relative group">
                  <Input
                    type="text"
                    id="payee-website"
                    name="website"
                    value={localFormData.website}
                    onChange={handleLocalInputChange}
                    placeholder={t('settings.payeesSettings.form.websitePlaceholder')}
                    maxLength={100}
                    className="h-11 rounded-xl border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-text-primary dark:text-text-dark-primary shadow-sm focus:ring-2 focus:ring-primary transition-all duration-200 pr-12"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-secondary">
                    {localFormData.website.length}/100
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="payee-notes" className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                  {t('common.notes')}
                  <Badge variant="secondary" className="bg-primary/10 text-primary dark:bg-primary/20">{t('common.optional')}</Badge>
                </Label>
                <div className="relative group">
                  <textarea
                    id="payee-notes"
                    name="notes"
                    value={localFormData.notes}
                    onChange={(e) => setLocalFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder={t('settings.payeesSettings.form.notesPlaceholder')}
                    maxLength={500}
                    className="w-full h-24 rounded-xl border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-text-primary dark:text-text-dark-primary shadow-sm focus:ring-2 focus:ring-primary transition-all duration-200 p-3 resize-none"
                  />
                  <span className="absolute right-3 bottom-3 text-xs text-text-secondary">
                    {localFormData.notes.length}/500
                  </span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <div className="flex gap-2 justify-end w-full">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onClose}
                  className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-text-primary dark:text-text-dark-primary hover:bg-background-secondary/90 dark:hover:bg-background-dark-secondary/90 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl h-11 transition-all duration-200"
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  type="submit"
                  disabled={!localFormData.name.trim()}
                  className="bg-primary text-white hover:bg-primary/90 shadow-sm rounded-xl h-11 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
                >
                  {mode === 'add' ? t('actions.add') : t('actions.save')}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  });
  PayeeDialog.displayName = 'PayeeDialog';

  const LoadingSkeleton = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-6 py-4">
        <Skeleton className="h-8 w-[200px] bg-background-secondary/10 dark:bg-background-dark-secondary/10 rounded-lg" />
        <Skeleton className="h-11 w-[150px] bg-background-secondary/10 dark:bg-background-dark-secondary/10 rounded-xl" />
      </div>
      <div className="px-6 pb-4">
        <Skeleton className="h-11 w-full bg-background-secondary/10 dark:bg-background-dark-secondary/10 rounded-xl" />
      </div>
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center justify-between px-6 py-4 bg-background-secondary/10 dark:bg-background-dark-secondary/10 border-b border-border dark:border-border-dark">
            <div className="flex items-center space-x-4">
              <Skeleton className="h-10 w-[200px] bg-background-secondary/10 dark:bg-background-dark-secondary/10 rounded-lg" />
              <Skeleton className="h-6 w-[100px] bg-background-secondary/10 dark:bg-background-dark-secondary/10 rounded-full" />
            </div>
            <div className="flex items-center space-x-4">
              <Skeleton className="h-8 w-[100px] bg-background-secondary/10 dark:bg-background-dark-secondary/10 rounded-lg" />
              <Skeleton className="h-8 w-[120px] bg-background-secondary/10 dark:bg-background-dark-secondary/10 rounded-lg" />
              <div className="flex space-x-2">
                <Skeleton className="h-8 w-8 bg-background-secondary/10 dark:bg-background-dark-secondary/10 rounded-lg" />
                <Skeleton className="h-8 w-8 bg-background-secondary/10 dark:bg-background-dark-secondary/10 rounded-lg" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const EmptyState = () => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-12 px-6"
    >
      <div className="w-16 h-16 mx-auto mb-6 relative">
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute inset-0 bg-primary/10 rounded-full"
        />
        <Building2 className="w-16 h-16 text-primary relative z-10" />
      </div>
      <motion.h3 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent mb-3"
      >
        {payees.length === 0 ? t('settings.payeesSettings.emptyTitle') : t('settings.payeesSettings.noResultsTitle')}
      </motion.h3>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-text-secondary mb-8 max-w-md mx-auto"
      >
        {payees.length === 0 
          ? t('settings.payeesSettings.emptyDescription')
          : t('settings.payeesSettings.noResultsDescription')}
      </motion.p>
      {payees.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            onClick={() => setIsAddDialogOpen(true)}
            className="bg-primary text-white hover:bg-primary/90 shadow-sm h-11 rounded-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('settings.payeesSettings.addFirst')}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );

  return (
    <div className="h-full flex flex-col space-y-4">
      <header className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl shadow-sm px-4 py-4 md:px-6 md:py-6 sticky top-3 z-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 md:mb-6">
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="p-2 bg-primary/10 rounded-xl">
              <Building2 className="w-5 h-5 text-primary dark:text-primary-dark" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                {t('settings.payeesSettings.title')}
              </h1>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                {t('settings.payeesSettings.description')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-end md:self-auto">
            <Button
              onClick={handleRefreshPayees}
              variant="outline"
              className="border-border dark:border-border-dark rounded-xl"
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {t('actions.refresh')}
            </Button>
            <Button
              onClick={handleExport}
              variant="outline"
              className="border-border dark:border-border-dark rounded-xl"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('actions.export')}
            </Button>
            <Button
              onClick={() => setIsAddDialogOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90 rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('settings.payeesSettings.addNew')}
            </Button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary dark:text-text-dark-secondary w-4 h-4" />
            <Input
              id="search-payees"
              placeholder={t('actions.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-border dark:border-border-dark rounded-xl h-11"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 hover:bg-background-secondary dark:hover:bg-background-dark-secondary rounded-lg"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className={`h-11 border-border dark:border-border-dark rounded-xl ${categoryFilter ? 'bg-primary/10 border-primary/30 text-primary dark:bg-primary-dark/10 dark:border-primary-dark/30 dark:text-primary-dark' : 'bg-background-secondary/50 dark:bg-background-dark-secondary/50'}`}
              >
                <Filter className="w-4 h-4 mr-2" />
                {categoryFilter ? t(`settings.payeesSettings.type.${categoryFilter}`) : t('actions.filter')}
                {categoryFilter && (
                  <Badge 
                    variant="secondary" 
                    className="ml-2 bg-primary/20 hover:bg-primary/30 text-primary dark:bg-primary-dark/20 dark:hover:bg-primary-dark/30 dark:text-primary-dark" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setCategoryFilter('');
                    }}
                  >
                    {t('actions.clear')}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark">
              <DropdownMenuLabel className="text-text-secondary dark:text-text-dark-secondary">{t('settings.payeesSettings.filterBy')}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-border dark:bg-border-dark" />
              <DropdownMenuItem 
                onClick={() => setCategoryFilter('')}
                className={`${!categoryFilter ? 'bg-primary/10 text-primary dark:bg-primary-dark/10 dark:text-primary-dark' : 'text-text-primary dark:text-text-dark-primary'}`}
              >
                {!categoryFilter && <Check className="w-4 h-4 mr-2" />}
                {t('settings.payeesSettings.all')}
              </DropdownMenuItem>
              {uniqueCategories.map(cat => (
                <DropdownMenuItem 
                  key={cat} 
                  onClick={() => setCategoryFilter(cat)}
                  className={`${categoryFilter === cat ? 'bg-primary/10 text-primary dark:bg-primary-dark/10 dark:text-primary-dark' : 'text-text-primary dark:text-text-dark-primary'}`}
                >
                  {categoryFilter === cat && <Check className="w-4 h-4 mr-2" />}
                  {t(`settings.payeesSettings.type.${cat}`)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex-1 overflow-auto px-4 pb-4">
        <AnimatePresence mode="wait">
          {isLoading ? (
            <LoadingSkeleton />
          ) : filteredPayees.length === 0 ? (
            <EmptyState />
          ) : (
            <motion.div 
              {...tableAnimation}
              className="h-full"
            >
              <Card className="h-full bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-sm">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader className="bg-background-secondary/50 dark:bg-background-dark-secondary/50">
                      <TableRow className="border-border dark:border-border-dark hover:bg-transparent">
                        <TableHead 
                          className="cursor-pointer text-text-secondary dark:text-text-dark-secondary"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center">
                            {t('common.name')}
                            {sortConfig?.key === 'name' && (
                              <ArrowUpDown 
                                className={`ml-1 h-4 w-4 ${sortConfig.direction === 'desc' ? 'rotate-180' : ''} transition-transform`}
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer text-text-secondary dark:text-text-dark-secondary"
                          onClick={() => handleSort('type')}
                        >
                          <div className="flex items-center">
                            {t('common.type')}
                            {sortConfig?.key === 'type' && (
                              <ArrowUpDown 
                                className={`ml-1 h-4 w-4 ${sortConfig.direction === 'desc' ? 'rotate-180' : ''} transition-transform`}
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer text-text-secondary dark:text-text-dark-secondary hidden md:table-cell"
                          onClick={() => handleSort('total_transactions')}
                        >
                          <div className="flex items-center">
                            {t('common.transactions')}
                            {sortConfig?.key === 'total_transactions' && (
                              <ArrowUpDown 
                                className={`ml-1 h-4 w-4 ${sortConfig.direction === 'desc' ? 'rotate-180' : ''} transition-transform`}
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer text-text-secondary dark:text-text-dark-secondary hidden md:table-cell"
                          onClick={() => handleSort('updated_at')}
                        >
                          <div className="flex items-center">
                            {t('common.lastTransaction')}
                            {sortConfig?.key === 'updated_at' && (
                              <ArrowUpDown 
                                className={`ml-1 h-4 w-4 ${sortConfig.direction === 'desc' ? 'rotate-180' : ''} transition-transform`}
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="text-right w-20 text-text-secondary dark:text-text-dark-secondary">{t('common.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPayees.map(payee => (
                        <motion.tr 
                          key={payee.id}
                          {...rowAnimation}
                          className="border-border dark:border-border-dark hover:bg-background-secondary dark:hover:bg-background-dark-secondary"
                        >
                          <TableCell className="font-medium text-text-primary dark:text-text-dark-primary">
                            {payee.name}
                          </TableCell>
                          <TableCell>
                            {payee.type ? (
                              <Badge variant="outline" className={`
                                ${payee.type === 'business' ? 'bg-success/5 text-success border-success/20' : ''}
                                ${payee.type === 'person' ? 'bg-primary/5 text-primary border-primary/20' : ''}
                                ${payee.type === 'utility' ? 'bg-warning/5 text-warning border-warning/20' : ''}
                                ${payee.type === 'subscription' ? 'bg-info/5 text-info border-info/20' : ''}
                                ${payee.type === 'general' ? 'bg-secondary/5 text-secondary border-secondary/20' : ''}
                              `}>
                                <span className="flex items-center gap-1.5">
                                  {payee.type === 'business' && <Building2 className="w-3 h-3" />}
                                  {payee.type === 'person' && <User className="w-3 h-3" />}
                                  {payee.type === 'utility' && <Zap className="w-3 h-3" />}
                                  {payee.type === 'subscription' && <CreditCard className="w-3 h-3" />}
                                  {payee.type === 'general' && <Tag className="w-3 h-3" />}
                                  {payee.type && t(`settings.payeesSettings.type.${payee.type}`)}
                                </span>
                              </Badge>
                            ) : (
                              <span className="text-text-secondary dark:text-text-dark-secondary text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-text-secondary dark:text-text-dark-secondary">
                            {payee.total_transactions || 0}
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-text-secondary dark:text-text-dark-secondary">
                            {payee.updated_at 
                              ? formatDateOnly(payee.updated_at) 
                              : <span className="text-sm">—</span>
                            }
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedPayee({...payee, isActive: true});
                                  setIsEditDialogOpen(true);
                                }}
                                className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/10 dark:text-primary-dark dark:hover:text-primary-dark dark:hover:bg-primary-dark/10 rounded-lg"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  payeeIdForDeletion.current = payee.id;
                                  setPayeeNameForDeletion(payee.name);
                                  setIsDeleteDialogOpen(true);
                                }}
                                className="h-8 w-8 text-destructive hover:text-destructive hover:bg-error/10 rounded-lg"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </motion.tr>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <PayeeDialog isOpen={isAddDialogOpen} mode="add" />
      <PayeeDialog isOpen={isEditDialogOpen} mode="edit" />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={(open) => {
        setIsDeleteDialogOpen(open);
        // Only clear selectedPayee if the dialog is being closed and we're not in the middle of a delete operation
        if (!open) {
          // Small delay to ensure we don't clear selectedPayee during delete operation
          setTimeout(() => {
            if (!isDeleteDialogOpen) {
              setSelectedPayee(null);
            }
          }, 100);
        }
      }}>
        <AlertDialogContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-text-primary dark:text-text-dark-primary">{t('settings.payeesSettings.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary dark:text-text-dark-secondary">
              {t('settings.payeesSettings.deleteDescription')} {payeeNameForDeletion ? `"${payeeNameForDeletion}"` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border dark:border-border-dark bg-background-secondary/50 dark:bg-background-dark-secondary/50 text-text-primary dark:text-text-dark-primary">
              {t('actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault(); // Prevent default to handle manually
                handleDeletePayee();
              }}
              className="bg-error hover:bg-error/90 text-white font-medium shadow-sm rounded-xl h-11 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            >
              {t('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
